import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UpdateFormDataActionWithId } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { KeycloakWrapperService } from '@app/finxone-web-frontend/app/lib/services/keycloak-wrapper-service/keycloak-wrapper.service';
import { MetadataService } from '@app/finxone-web-frontend/app/lib/services/metadata-service/metadata-service.service';
import { SecurePayloadDecodedResponse } from '@app/finxone-web-frontend/app/lib/services/metadata-service/metadata-service.type';
import { InvitationService } from '@app/finxone-web-frontend/app/lib/services/invitation-service/invitation.service';
import { ProjectSettingsState } from '@app/finxone-web-frontend/app/lib/state/project-settings.state';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import { GetProfile } from '@app/finxone-web-frontend/app/lib/actions/user-profile.action';
import { FormID } from '@finxone-platform/form-action';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  ActiveDevice,
  ProjectConfiguration,
  VerifyInvitationResponse,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { SecureTokenType } from '@finxone-platform/shared/services/secure-token';
import { DeviceDetectorService } from 'ngx-device-detector';
import { EMPTY, Subject, filter, switchMap, take, takeUntil } from 'rxjs';
import { SetRole } from '@app/finxone-web-frontend/app/lib/actions/role.action';
import { RoleState } from '@app/finxone-web-frontend/app/lib/state/role.state';

type UserStatus = 'newUser' | 'existingUser';
type SessionStatus = 'matchingUser' | 'differentUser' | 'noSession';

@Component({
  selector: 'finxone-web-frontend-invitation-redirection-handler',
  template: `
    @if (isLoading) {
      <div id="loader" class="loader-container">
        <div class="loader"></div>
      </div>
    }
  `,
})
export class InvitationRedirectionHandlerComponent implements OnInit {
  @AutoDestroy private readonly destroy$: Subject<void> = new Subject<void>();
  private readonly activeRoute = inject(ActivatedRoute);
  private readonly metadataService = inject(MetadataService);
  private readonly invitationService = inject(InvitationService);
  private readonly keycloakWrapperService = inject(KeycloakWrapperService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly deviceDetectorService = inject(DeviceDetectorService);

  public isLoading = false;
  private isExistingUser = false;
  private token = '';
  private decodedTokenInfo: SecurePayloadDecodedResponse;
  private projectConfig: ProjectConfiguration | undefined;
  private invitationDetails: VerifyInvitationResponse;

  ngOnInit(): void {
    this.projectConfig = this.store.selectSnapshot(ProjectSettingsState.getProjectSettings)?.projectConfig;
    this.readParamsFromUrlAndDecode();
  }

  private readParamsFromUrlAndDecode(): void {
    this.activeRoute.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const token = params.get('token');
      if (token !== null) {
        this.token = token;
        this.resolveTokenAndHandleRedirection(token);
      }
    });
  }

  private resolveTokenAndHandleRedirection(token: string): void {
    this.metadataService
      .resolveSecurePayload(token)
      .pipe(
        switchMap((response) => {
          this.decodedTokenInfo = response;

          // An expired link is turned away here, on the expiry carried by the
          // token itself, so no verify call is made for it.
          if (this.isTokenExpired(response)) {
            this.redirectToExpiredInvitation();
            return EMPTY;
          }

          return this.invitationService.verifyInvitation(token);
        }),
      )
      .subscribe({
        next: (invitationDetails) => {
          this.invitationDetails = invitationDetails;
          this.isExistingUser = invitationDetails.isUserExisting;

          // Checked again against the invitation itself: its expiry date can
          // differ from the token's if the project's expiry window changed.
          if (new Date(invitationDetails.expiresAt) < new Date()) {
            this.redirectToExpiredInvitation();
            return;
          }

          this.handleRedirectionByReadingType();
        },
        // An invitation that has expired or was already actioned is rejected by
        // the API, so the expired page is reached from here rather than from the
        // expiry date above.
        error: () => this.redirectToExpiredInvitation(),
      });
  }

  private isTokenExpired(decodedToken: SecurePayloadDecodedResponse): boolean {
    return !!decodedToken.expiresAt && new Date(decodedToken.expiresAt) < new Date();
  }

  private redirectToExpiredInvitation(): void {
    const activeDevice = this.getActiveDevice();
    const expiredInvitationUrl =
      this.projectConfig?.jointAccountInvitationRedirectionConfig?.[activeDevice]?.expiredInvitation;
    if (expiredInvitationUrl) {
      this.router.navigateByUrl(expiredInvitationUrl);
    }
  }

  private getActiveDevice(): ActiveDevice {
    // Prefer the device held in role state over the raw device-detector value: role state stores the device
    // view the workflow is actually rendered for (a tablet is resolved down to 'mobile' when Tablet View is
    // disabled for the project), so it's the one the redirection config is keyed by.
    const roleStateActiveDevice = this.store.selectSnapshot(RoleState.getRole)?.device as ActiveDevice;
    let activeDevice = this.deviceDetectorService.deviceType as ActiveDevice;
    if (roleStateActiveDevice) {
      activeDevice = roleStateActiveDevice;
    }
    return activeDevice;
  }

  private getRedirectUrlBySecureTokenType(type: SecureTokenType.JOINT_ACCOUNT_INVITATION) {
    const userType: UserStatus = this.isExistingUser ? 'existingUser' : 'newUser';
    const activeDevice = this.getActiveDevice();
    const urlDirectory = {
      [SecureTokenType.JOINT_ACCOUNT_INVITATION]:
        this.projectConfig?.jointAccountInvitationRedirectionConfig?.[activeDevice]?.[userType],
    };
    return urlDirectory[type];
  }

  private handleRedirectionByReadingType(): void {
    const type = this.decodedTokenInfo.type;
    if (type === SecureTokenType.JOINT_ACCOUNT_INVITATION) {
      const url = this.getRedirectUrlBySecureTokenType(type);
      if (!url?.length) return;
      this.handleJointAccountInvitationFlow(url);
    }
  }

  private handleJointAccountInvitationFlow(redirectUrl: string) {
    sessionStorage.setItem('jointAccountInvitationId', this.decodedTokenInfo.invitationId);
    sessionStorage.setItem('jointAccountInvitationToken', this.token);
    if (this.isExistingUser) {
      this.handleExistingUserRedirection(redirectUrl);
    } else {
      this.handleNewUserRedirection(redirectUrl);
    }
  }

  private handleExistingUserRedirection(redirectUrl: string): void {
    const sessionStatusHandlers: Record<SessionStatus, () => void> = {
      // Wrong user logged in: end their session, then return to this
      // invitation link so the flow re-runs and prompts the invitee to login
      differentUser: () => this.keycloakWrapperService.logout(window.location.href),
      matchingUser: () => this.loadProfileAndNavigate(redirectUrl),
      // Redirect back to this invitation URL after login so this flow re-runs
      // with an authenticated session and verifies the logged in user's email
      noSession: () =>
        this.keycloakWrapperService.login({
          redirectUri: window.location.href,
          loginHint: this.invitationDetails.inviteeEmail,
        }),
    };
    const status = this.getLoggedInUserSessionStatus();
    sessionStatusHandlers[status]();
  }

  private handleNewUserRedirection(redirectUrl: string): void {
    // Store the values to be used in the onboarding form later on
    this.store.dispatch(
      new UpdateFormDataActionWithId(
        {
          productCode: this.invitationDetails.productCode,
          email: this.invitationDetails.inviteeEmail,
        },
        FormID.ONBOARDING_FLOW,
      ),
    );
    this.router.navigateByUrl(redirectUrl);
  }

  private loadProfileAndNavigate(redirectUrl: string): void {
    // /invitations is an unauthenticated route, so the normal boot sequence skips
    // GetProfile. We must load the profile explicitly before navigating to an
    // authenticated page that depends on it.
    this.isLoading = true;
    this.store.dispatch(new GetProfile());
    this.store
      .select(ProfileState.getProfile)
      .pipe(
        filter((profile) => !!profile?.email),
        take(1),
      )
      .subscribe((profile) => {
        this.isLoading = false;
        this.store.dispatch(new SetRole(profile?.activeRole ?? ''));
        this.router.navigateByUrl(redirectUrl);
      });
  }

  private getLoggedInUserSessionStatus(): SessionStatus {
    const isLoggedIn = this.keycloakWrapperService.isLoggedIn();
    const token = sessionStorage.getItem('token');

    if (isLoggedIn && token !== null) {
      // Read the email from the freshly issued keycloak token rather than
      // ProfileState, which may hold stale data persisted from a previous user
      const tokenEmail = this.keycloakWrapperService.getKeycloakInstance().tokenParsed?.['email'];
      if (!tokenEmail) return 'noSession';
      return tokenEmail === this.invitationDetails.inviteeEmail ? 'matchingUser' : 'differentUser';
    }
    return 'noSession';
  }
}
